"use client";

// WidgetSettingsEditor — schema-driven user settings editor for one widget.
//
// Reads the widget's settingsSchema, merges with stored values, renders a
// control per field (text / number / boolean / select). Writes back via
// useWidgetSettings hook → localStorage + event → other consumers re-render.
//
// Pattern mirrors FieldEditor for Blocks. This one is widget-scoped: lives
// inside SidebarWidgetInspector Tab 3 between Actions and Preview, and only
// renders when the selected widget declares a settingsSchema.

import { RotateCcw } from "lucide-react";
import {
  useWidgetSettings,
  clearWidgetSettings,
  type WidgetFieldSchema,
} from "@/lib/widgets/widgetSettings";
import { useSettings } from "@/contexts/SettingsContext";

// 8-color palette mirroring the Icon Library hub's BRAND_V2_PALETTE.
// Inline here (no shared module yet) to avoid pulling in the bigger import.
const COLOR_PALETTE: Array<{ name: string; value: string }> = [
  { name: "teal", value: "#14b8a6" },
  { name: "blue", value: "#3b82f6" },
  { name: "violet", value: "#8b5cf6" },
  { name: "amber", value: "#f59e0b" },
  { name: "rose", value: "#f43f5e" },
  { name: "emerald", value: "#10b981" },
  { name: "slate", value: "#64748b" },
  { name: "stone", value: "#78716c" },
];

interface WidgetSettingsEditorProps {
  widgetId: string;
  schema: WidgetFieldSchema[];
}

export function WidgetSettingsEditor({ widgetId, schema }: WidgetSettingsEditorProps) {
  const { language } = useSettings();
  const langKey: "he" | "en" | "ru" = language === "en" ? "en" : language === "ru" ? "ru" : "he";
  const isRtl = language === "he";
  const [settings, setKey] = useWidgetSettings(widgetId, schema);

  if (schema.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">
          {isRtl ? "הגדרות הווידג'ט" : "Widget settings"}
        </div>
        <button
          type="button"
          onClick={() => clearWidgetSettings(widgetId)}
          title={isRtl ? "אפס לברירות מחדל" : "Reset to defaults"}
          className="inline-flex items-center gap-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-1.5 py-0.5 text-[9px] text-neutral-400 hover:bg-white/[0.04] hover:text-neutral-200 transition-colors"
        >
          <RotateCcw className="h-2.5 w-2.5" />
          {isRtl ? "אפס" : "Reset"}
        </button>
      </div>
      {schema.map((field) => (
        <FieldControl
          key={field.key}
          field={field}
          value={settings[field.key]}
          onChange={(v) => setKey(field.key, v)}
          langKey={langKey}
        />
      ))}
    </div>
  );
}

interface FieldControlProps {
  field: WidgetFieldSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  langKey: "he" | "en" | "ru";
}

function FieldControl({ field, value, onChange, langKey }: FieldControlProps) {
  const label = pickLang(field.label, langKey);
  const description = field.description ? pickLang(field.description, langKey) : null;
  const inputClass =
    "w-full rounded-md border border-white/[0.08] bg-white/[0.02] px-2 py-1 text-[11px] text-neutral-200 focus:border-[var(--cc-accent-400)] focus:outline-none";

  return (
    <label className="block space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-neutral-300">{label}</span>
        {field.type === "boolean" && (
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="accent-[var(--cc-accent-500)] cursor-pointer"
          />
        )}
      </div>
      {description && (
        <p className="text-[10px] text-neutral-600 leading-tight">{description}</p>
      )}
      {field.type === "text" && (
        <input
          type="text"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
      )}
      {field.type === "number" && (
        <input
          type="number"
          value={typeof value === "number" ? value : ""}
          onChange={(e) => {
            const next = e.target.value === "" ? null : Number(e.target.value);
            onChange(next);
          }}
          className={inputClass}
        />
      )}
      {field.type === "select" && field.options && (
        <select
          value={typeof value === "string" ? value : (typeof field.default === "string" ? field.default : "")}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputClass} cursor-pointer`}
        >
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {pickLang(opt.label, langKey)}
            </option>
          ))}
        </select>
      )}
      {field.type === "url" && (
        <input
          type="url"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://"
          className={inputClass}
          dir="ltr"
        />
      )}
      {field.type === "color" && (
        <div className="flex flex-wrap gap-1.5">
          {COLOR_PALETTE.map((swatch) => {
            const isActive = value === swatch.value;
            return (
              <button
                key={swatch.name}
                type="button"
                onClick={() => onChange(swatch.value)}
                title={swatch.name}
                aria-pressed={isActive}
                className={`h-5 w-5 rounded-md border-2 transition-all ${
                  isActive ? "border-white scale-110" : "border-white/10 hover:border-white/40"
                }`}
                style={{ background: swatch.value }}
              />
            );
          })}
        </div>
      )}
      {field.type === "multiselect" && field.options && (
        <div className="space-y-1">
          {field.options.map((opt) => {
            const arr = Array.isArray(value) ? (value as string[]) : [];
            const checked = arr.includes(opt.value);
            return (
              <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? Array.from(new Set([...arr, opt.value]))
                      : arr.filter((v) => v !== opt.value);
                    onChange(next);
                  }}
                  className="accent-[var(--cc-accent-500)] cursor-pointer"
                />
                <span className="text-[11px] text-neutral-300">{pickLang(opt.label, langKey)}</span>
              </label>
            );
          })}
        </div>
      )}
    </label>
  );
}

function pickLang(
  label: { he: string; en: string; ru?: string },
  langKey: "he" | "en" | "ru",
): string {
  if (langKey === "en") return label.en;
  if (langKey === "ru") return label.ru ?? label.en;
  return label.he;
}
